import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Monitor, Loader2, Search, MoreHorizontal, Upload, CheckCircle2, XCircle, ShieldX, ShieldCheck,
  Filter, ChevronRight, ChevronDown, X,
} from 'lucide-react';

import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/data-table/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import {
  useDevices, useUpdateDevice, useImportCsv, type Device, type DeviceStatus,
} from '@/hooks/use-devices';
import { useAddressBooksAll } from '@/hooks/use-address-books';
import { toast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api-client';
import { relativeTime, shortId, cn } from '@/lib/utils';
import { ExportCsvButton } from '@/components/export-csv-button';

const searchSchema = z.object({
  status: z.enum(['ACTIVE', 'PENDING_APPROVAL', 'BLOCKED']).optional(),
});

export const Route = createFileRoute('/_app/devices')({
  validateSearch: (search) => searchSchema.parse(search),
  component: DevicesPage,
});

function DevicesPage() {
  const initial = Route.useSearch();
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [status, setStatus] = React.useState<DeviceStatus | 'all'>(initial.status ?? 'all');
  const [onlineFilter, setOnlineFilter] = React.useState<'all' | 'true' | 'false'>('all');
  const [tagsInput, setTagsInput] = React.useState('');
  const [editingDevice, setEditingDevice] = React.useState<Device | null>(null);
  const [importing, setImporting] = React.useState(false);
  const [expanded, setExpanded] = React.useState<string | null>(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);

  const { data, isLoading } = useDevices({
    page,
    pageSize: 25,
    search: debouncedSearch || undefined,
    status: status === 'all' ? undefined : status,
    online: onlineFilter === 'all' ? undefined : onlineFilter === 'true',
    tags: tags.length > 0 ? tags : undefined,
  });

  return (
    <>
      <PageHeader
        title="Devices"
        description="End-user machines reachable via RustDesk. New devices appear via heartbeat or CSV import."
        actions={
          <>
            <ExportCsvButton domain="devices" />
            <Button onClick={() => setImporting(true)}>
              <Upload />
              <span>Import CSV</span>
            </Button>
          </>
        }
      />

      <Card className="mb-4 p-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Alias, hostname, RustDesk ID…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-8"
            />
          </div>
          <Select value={status} onValueChange={(v: DeviceStatus | 'all') => { setStatus(v); setPage(1); }}>
            <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="PENDING_APPROVAL">Pending approval</SelectItem>
              <SelectItem value="BLOCKED">Blocked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={onlineFilter} onValueChange={(v: 'all' | 'true' | 'false') => { setOnlineFilter(v); setPage(1); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Online & offline</SelectItem>
              <SelectItem value="true">Online only</SelectItem>
              <SelectItem value="false">Offline only</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Tags (comma-separated)"
            value={tagsInput}
            onChange={(e) => { setTagsInput(e.target.value); setPage(1); }}
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-6" />
              <TableHead>Device</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Address book</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="h-32 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                <Monitor className="mx-auto h-5 w-5 mb-2 opacity-50" />
                No devices match the current filters.
              </TableCell></TableRow>
            ) : (
              data?.data.map((d) => (
                <DeviceRow
                  key={d.id}
                  device={d}
                  expanded={expanded === d.id}
                  onToggle={() => setExpanded(expanded === d.id ? null : d.id)}
                  onEdit={() => setEditingDevice(d)}
                />
              ))
            )}
          </TableBody>
        </Table>
        {data && (
          <div className="border-t">
            <Pagination page={page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <EditDeviceDialog device={editingDevice} onOpenChange={(o) => !o && setEditingDevice(null)} />
      <ImportCsvDialog open={importing} onOpenChange={setImporting} />
    </>
  );
}

function DeviceRow({ device, expanded, onToggle, onEdit }: {
  device: Device; expanded: boolean; onToggle: () => void; onEdit: () => void;
}) {
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </TableCell>
        <TableCell>
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {device.alias || device.hostname || '—'}
            </span>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="id-pill">{device.rustdeskId}</span>
              {device.os && <span className="font-mono text-2xs text-muted-foreground">{device.os}{device.osVersion ? ` ${device.osVersion}` : ''}</span>}
            </div>
          </div>
        </TableCell>
        <TableCell>
          <DeviceStatusBadge status={device.status} online={device.online} />
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-1 max-w-[200px]">
            {device.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="font-mono text-2xs">{tag}</Badge>
            ))}
            {device.tags.length > 3 && (
              <Badge variant="outline" className="font-mono text-2xs">+{device.tags.length - 3}</Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-sm">
          {device.addressBook ? device.addressBook.name : <span className="text-muted-foreground">—</span>}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground tabular-nums">{relativeTime(device.lastSeenAt)}</TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <DeviceRowMenu device={device} onEdit={onEdit} />
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/30 py-4">
            <div className="grid gap-4 lg:grid-cols-3 text-xs font-mono">
              <div className="space-y-1">
                <Field label="Device ID" value={device.id} />
                <Field label="RustDesk ID" value={device.rustdeskId} />
                <Field label="Hostname" value={device.hostname ?? '—'} />
                <Field label="IP" value={device.ip ?? '—'} />
              </div>
              <div className="space-y-1">
                <Field label="OS" value={device.os ?? '—'} />
                <Field label="OS version" value={device.osVersion ?? '—'} />
                <Field label="RustDesk v." value={device.rustdeskVersion ?? '—'} />
                <Field label="Groups" value={device.groups.length === 0 ? '—' : device.groups.map((g) => g.group.name).join(', ')} />
              </div>
              <div className="space-y-1">
                {device.notes && (
                  <>
                    <p className="text-muted-foreground">Notes</p>
                    <p className="font-sans text-sm whitespace-pre-wrap">{device.notes}</p>
                  </>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function DeviceStatusBadge({ status, online }: { status: DeviceStatus; online: boolean }) {
  if (status === 'BLOCKED') {
    return <Badge variant="destructive">Blocked</Badge>;
  }
  if (status === 'PENDING_APPROVAL') {
    return <Badge variant="warning">Pending</Badge>;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('status-dot', online ? 'status-dot--online' : 'status-dot--offline')} />
      <span className="text-xs">{online ? 'Online' : 'Offline'}</span>
    </span>
  );
}

function DeviceRowMenu({ device, onEdit }: { device: Device; onEdit: () => void }) {
  const update = useUpdateDevice();

  function setStatus(status: DeviceStatus): void {
    update.mutate(
      { id: device.id, input: { status } },
      {
        onSuccess: () => toast({ title: `Device ${status === 'ACTIVE' ? 'approved' : status.toLowerCase()}` }),
        onError: (err) => toast({
          variant: 'destructive',
          title: 'Update failed',
          description: err instanceof ApiError ? err.message : 'Try again',
        }),
      },
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon"><MoreHorizontal /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
        {device.status === 'PENDING_APPROVAL' && (
          <DropdownMenuItem onSelect={() => setStatus('ACTIVE')}>
            <ShieldCheck /><span>Approve</span>
          </DropdownMenuItem>
        )}
        {device.status === 'ACTIVE' && (
          <DropdownMenuItem destructive onSelect={() => setStatus('BLOCKED')}>
            <ShieldX /><span>Block</span>
          </DropdownMenuItem>
        )}
        {device.status === 'BLOCKED' && (
          <DropdownMenuItem onSelect={() => setStatus('ACTIVE')}>
            <ShieldCheck /><span>Unblock</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground shrink-0 w-24">{label}</span>
      <span className="truncate">{value}</span>
    </div>
  );
}

// ============================================================
// Edit dialog
// ============================================================

const editSchema = z.object({
  alias: z.string().max(200).optional(),
  notes: z.string().max(2000).optional(),
  tagsInput: z.string().max(500).optional(),
  addressBookId: z.string().optional(),
});
type EditValues = z.infer<typeof editSchema>;

function EditDeviceDialog({ device, onOpenChange }: { device: Device | null; onOpenChange: (o: boolean) => void }) {
  const update = useUpdateDevice();
  const addressBooks = useAddressBooksAll();
  const form = useForm<EditValues>({ resolver: zodResolver(editSchema) });

  React.useEffect(() => {
    if (device) {
      form.reset({
        alias: device.alias ?? '',
        notes: device.notes ?? '',
        tagsInput: device.tags.join(', '),
        addressBookId: device.addressBookId ?? '__none__',
      });
    }
  }, [device, form]);

  if (!device) return null;

  function onSubmit(values: EditValues): void {
    const tags = (values.tagsInput ?? '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    update.mutate(
      {
        id: device!.id,
        input: {
          alias: values.alias || null,
          notes: values.notes || null,
          tags,
          addressBookId: values.addressBookId === '__none__' ? null : values.addressBookId,
        },
      },
      {
        onSuccess: () => { toast({ title: 'Device updated' }); onOpenChange(false); },
        onError: (err) => toast({
          variant: 'destructive',
          title: 'Update failed',
          description: err instanceof ApiError ? err.message : 'Try again',
        }),
      },
    );
  }

  return (
    <Dialog open={!!device} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit device</DialogTitle>
          <DialogDescription>
            <span className="id-pill">{device.rustdeskId}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="d-alias">Alias</Label>
            <Input id="d-alias" placeholder="POS-Boulangerie-01" {...form.register('alias')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="d-tags">Tags</Label>
            <Input id="d-tags" placeholder="comma, separated, tags" {...form.register('tagsInput')} />
            <p className="text-2xs text-muted-foreground">Lowercase, up to 32 unique tags.</p>
          </div>
          <div className="space-y-2">
            <Label>Address book</Label>
            <Select
              value={form.watch('addressBookId') ?? '__none__'}
              onValueChange={(v) => form.setValue('addressBookId', v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Unassigned —</SelectItem>
                {addressBooks.data?.data.map((ab) => (
                  <SelectItem key={ab.id} value={ab.id}>{ab.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="d-notes">Notes</Label>
            <textarea
              id="d-notes"
              {...form.register('notes')}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending && <Loader2 className="animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Import CSV dialog
// ============================================================

function ImportCsvDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [csvContent, setCsvContent] = React.useState('');
  const [result, setResult] = React.useState<Awaited<ReturnType<ReturnType<typeof useImportCsv>['mutateAsync']>> | null>(null);
  const importMut = useImportCsv();
  const addressBooks = useAddressBooksAll();
  const [defaultAB, setDefaultAB] = React.useState<string>('__none__');

  React.useEffect(() => {
    if (open) {
      setCsvContent('');
      setResult(null);
      setDefaultAB('__none__');
    }
  }, [open]);

  function onFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ variant: 'destructive', title: 'File too large', description: 'Max 10 MB.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCsvContent(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.readAsText(file);
  }

  function onSubmit(): void {
    importMut.mutate(
      {
        csvContent,
        defaultAddressBookId: defaultAB === '__none__' ? undefined : defaultAB,
      },
      {
        onSuccess: (res) => {
          setResult(res);
          if (res.failed.length === 0) {
            toast({ title: `Imported ${res.imported}, updated ${res.updated}`, variant: 'success' });
          } else {
            toast({
              variant: 'destructive',
              title: `${res.failed.length} row${res.failed.length > 1 ? 's' : ''} failed`,
              description: 'See details in the dialog.',
            });
          }
        },
        onError: (err) => toast({
          variant: 'destructive',
          title: 'Import failed',
          description: err instanceof ApiError ? err.message : 'Try again',
        }),
      },
    );
  }

  const csvPreview = csvContent.split('\n').slice(0, 5).join('\n');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import devices from CSV</DialogTitle>
          <DialogDescription>
            Columns: rustdeskId, alias, hostname, addressBookName, tags. The first row is the header.
            New address books are created on the fly when an unknown name is referenced.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>CSV file (or paste below)</Label>
              <Input type="file" accept=".csv,text/csv" onChange={onFile} />
            </div>
            <div className="space-y-2">
              <Label>Or paste content</Label>
              <textarea
                value={csvContent}
                onChange={(e) => setCsvContent(e.target.value)}
                placeholder={'rustdeskId,alias,hostname,addressBookName,tags\n123456789,POS-01,bakery-pc,Bakery Inc.,pos windows'}
                className="flex min-h-[140px] w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              {csvContent && (
                <p className="text-2xs text-muted-foreground tabular-nums">
                  {csvContent.split('\n').filter((l) => l.trim()).length - 1} data row(s) detected
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Default address book (if column is empty)</Label>
              <Select value={defaultAB} onValueChange={setDefaultAB}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {addressBooks.data?.data.map((ab) => (
                    <SelectItem key={ab.id} value={ab.id}>{ab.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={onSubmit} disabled={!csvContent || importMut.isPending}>
                {importMut.isPending && <Loader2 className="animate-spin" />}
                Import
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <ResultCard label="Imported" value={result.imported} icon={CheckCircle2} color="text-success" />
              <ResultCard label="Updated" value={result.updated} icon={CheckCircle2} color="text-primary" />
              <ResultCard label="Failed" value={result.failed.length} icon={XCircle} color={result.failed.length > 0 ? 'text-destructive' : 'text-muted-foreground'} />
            </div>
            {result.failed.length > 0 && (
              <div className="border rounded-md max-h-60 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>RustDesk ID</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.failed.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs tabular-nums">{f.row}</TableCell>
                        <TableCell className="font-mono text-xs">{f.rustdeskId ?? '—'}</TableCell>
                        <TableCell className="text-sm text-destructive">{f.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
              <Button onClick={() => { setResult(null); setCsvContent(''); }}>Import another</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultCard({ label, value, icon: Icon, color }: {
  label: string; value: number; icon: React.ComponentType<{ className?: string }>; color: string;
}) {
  return (
    <Card className="p-4">
      <div className={cn('flex items-center gap-2 mb-1', color)}>
        <Icon className="h-4 w-4" />
        <span className="font-mono text-2xs uppercase tracking-wider">{label}</span>
      </div>
      <p className={cn('font-display text-2xl tabular-nums', color)}>{value}</p>
    </Card>
  );
}
